import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Inject, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { UserService } from "@app2/account/user.service";
import { TicketClientService } from "@app2/clients/ticket-client.service";
import { DateTimeTz } from "@app2/shared/components/date-time-tz-picker.component";
import { DsRichTextEditorComponent } from "@app2/shared/components/ds-rich-text-editor.component";
import { SeverityEditComponent } from "@app2/shared/components/severity-edit.component";
import { FeaturesService } from "@app2/shared/services/features.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { TimeService } from "@app2/shared/services/time.service";
import { Ticket, TicketTemplate } from "@app2/type-defs/ticket/ticket-types";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { cloneDeep } from "lodash-es";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import * as _ from "underscore";

interface DialogData {
    ticket: Ticket;
    modalTitleKey: string;
    // Used from the Universal Inbox to create tickets in any of the authorized orgs
    ticketOrgId?: uuid;
}

@UntilDestroy()
@Component({
    selector: "add-edit-ticket-modal",
    template: `
        <span mat-dialog-title>
            {{ data.modalTitleKey | localize }}
        </span>
        <mat-dialog-content>
            <article id="ticket-add-edit" class="nice-form">
                <form #ticketAddEditForm="ngForm">
                    <h1>{{'INFO' | localize}}</h1>
                    <section>
                        <label>
                            <div class="title-header">{{('TITLE' | localize)}} *</div>
                        </label>
                        <input type="text"
                               [(ngModel)]="ticketCopy.summary"
                               name="summary"
                               required
                               [placeholder]="'INCIDENT_TITLE_PLACEHOLDER' | localize"
                               test-id="ticket-summary"/>

                        <label>{{'OWNER' | localize}}</label>
                        <person-picker [placeholder]="'PLACEHOLDER_SELECT_TICKET_OWNER' | localize"
                                       [selectedId]="ticketCopy.ownedBy"
                                       (selectedIdChange)="setNewOwner($event)"
                                       test-id="ticket-owner">
                        </person-picker>

                        <span *ngIf="ticketCopy.slugName === 'incident'">
                            <label>{{'SEVERITY' | localize}}</label>
                            <severity-edit [(severity)]="ticketCopy.severity"
                                           (formValidityChange)="severityFormValidity = $event"
                                           test-id="ticket-severity">
                            </severity-edit>
                        </span>
                    </section>

                    <ng-container *ngIf="isNew">
                        <h1>{{'WATCHERS' | localize}}</h1>
                        <section>
                            <label>
                                <div class="title-header"></div>
                            </label>
                            <ticket-watchers-edit [(watchers)]="ticketCopy.watchers"
                                                  [editPerm]="ticketCopy.slugName + '_EDIT'"
                                                  test-id="ticket-watchers">
                            </ticket-watchers-edit>
                        </section>
                    </ng-container>

                    <ng-container *ngIf="isNew">
                        <h1>{{'DUE_DATE' | localize}}</h1>
                        <section>
                            <label>
                                <div class="title-header"></div>
                            </label>
                            <div class="half-width">
                                <button *ngIf="!ticketCopy.dueOn"
                                        class="link inline-add nice-form-display-text"
                                        test-id="add-ticket-due-date"
                                        (click)="addTicketDue()">
                                    <i class="ion-plus"></i>
                                    {{ "SET_DUE_DATE" | localize}}
                                </button>
                                <ng-container *ngIf="ticketCopy.dueOn">
                                    <date-time-tz-picker [datetime]="ticketCopy.dueOn"
                                                         [timezone]="ticketCopy.timeZoneId"
                                                         (onChanged)="updateTicketDue($event)"
                                                         test-id="ticket-due-date">
                                    </date-time-tz-picker>
                                    <action-buttons inline="true"
                                                    (cancel)="removeTicketDue()">
                                    </action-buttons>
                                </ng-container>
                            </div>
                        </section>
                    </ng-container>

                    <div *ngIf="ticketCopy.slugName === 'task' && ticketCopy.taskData && !this.isNew">
                        <h1>{{'SUMMARY_NOTE' | localize}}</h1>
                        <section class="extra-margin-top">
                            <textarea rows="5"
                                      [(ngModel)]="ticketCopy.taskData.summaryNote"
                                      name="summaryNote"
                                      [placeholder]="'TICKET_SUMMARY_NOTE_PLACEHOLDER' | localize"
                                      test-id="ticket-summary-note">
                        </textarea>
                        </section>
                    </div>

                    <h1>{{'DESCRIPTION' | localize}} {{ticketCopy.slugName === "incident" ? '*' : ''}}</h1>
                    <section class="extra-margin-top">
                        <ds-rich-text-editor *ngIf="usingNewEditor"
                                             [(content)]="ticketCopy.description"
                                             [placeholder]="'INCIDENT_DESCRIPTION_PLACEHOLDER' | localize"
                                             name="description"
                                             test-id="rich-ticket-description"
                                             #richTextEditor></ds-rich-text-editor>
                        <textarea *ngIf="!usingNewEditor"
                                  rows="15"
                                  [(ngModel)]="ticketCopy.description"
                                  name="description"
                                  [required]="ticketCopy.slugName === 'incident'"
                                  [placeholder]="'INCIDENT_DESCRIPTION_PLACEHOLDER' | localize"
                                  test-id="ticket-description">
                        </textarea>
                        <add-ticket-description-template-dropdown *dsPermissionsRequired="'TRAC_USER'"
                                                                  (selected)="updateDescription($event)">
                        </add-ticket-description-template-dropdown>
                    </section>
                </form>
            </article>
        </mat-dialog-content>
        <mat-dialog-actions>
            <modal-buttons [canSave]="(ticketFormValidity$ | async) && this.severityFormValidity &&
                                      (ticketCopy?.slugName === 'incident' && this.ticketCopy?.description.length > 0)"
                           (onCancel)="onCancel()"
                           (onSave)="onSave()"
                           [saveInProgress]="saving">
            </modal-buttons>
        </mat-dialog-actions>
    `,
    styles: [`
        .title-header {
            margin-top: -10px;
        }
    `],
})
export class AddEditTicketModalComponent implements AfterViewInit {

    @ViewChild("ticketAddEditForm") ticketAddEditForm;
    @ViewChild(SeverityEditComponent) severityEdit;
    @ViewChild("richTextEditor") richTextEditor: DsRichTextEditorComponent;

    ticketCopy: Ticket;
    currentUserId: uuid;

    isNew: boolean; // isNew is true when the ticket is totally new or a clone
    ticketFormValidity$: Observable<boolean>;
    severityFormValidity: boolean;
    saving: boolean;

    usingNewEditor: boolean;

    @HostListener("window:beforeunload", ["$event"])
    public onPageUnload($event: BeforeUnloadEvent) {
            $event.returnValue = "showConfirmation";
    }

    constructor(private dialogRef: MatDialogRef<AddEditTicketModalComponent>,
                @Inject(MAT_DIALOG_DATA) public data: DialogData,
                private readonly userService: UserService,
                private readonly ticketClient: TicketClientService,
                private readonly notificationsService: NotificationsService,
                private readonly timeService: TimeService,
                private readonly featureService: FeaturesService,
                private cdr: ChangeDetectorRef) {
        this.ticketCopy = cloneDeep(this.data.ticket);
        this.currentUserId = this.userService.getCurrentUser().id;
        this.isNew = !this.ticketCopy.id;
    }

    ngAfterViewInit() {
        this.featureService.getFeature$("newEditor")
            .pipe(untilDestroyed(this))
            .subscribe(newEditor => this.usingNewEditor = newEditor);

        this.ticketFormValidity$ = this.ticketAddEditForm.statusChanges
            .pipe(
                startWith(false),
                map(formValidity => formValidity === "VALID"),
            );
        this.cdr.detectChanges();
    }

    onSave() {
        // Creating or cloning a ticket
        this.saving = true;

        if (this.isNew) {
            this.ticketClient.createTicket(this.ticketCopy, this.data.ticketOrgId)
                .then(ticket => this.dialogRef.close(ticket))
                .catch(error => this.ticketClient.handleTicketUpdateError(error))
                .finally(() => this.saving = false);
            return;
        }

        // Editing an existing ticket
        const updates = {
            watchers: this.ticketCopy.watchers,
            description: this.ticketCopy.description,
            ownedBy: this.ticketCopy.ownedBy,
            summary: this.ticketCopy.summary,
            dueOn: this.ticketCopy.dueOn,
            timeZoneId: this.ticketCopy.timeZoneId,
            severity: this.ticketCopy.severity,
        };
        if (!!this.ticketCopy.taskData) {
            _.extend(updates, { task_data: { summaryNote: this.ticketCopy.taskData.summaryNote } });
        }
        this.ticketClient.updateTicket(this.ticketCopy, updates)
            .then(ticket => this.dialogRef.close(ticket))
            .catch(error => this.ticketClient.handleTicketUpdateError(error))
            .finally(() => this.saving = false);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    public setNewOwner(owner: string) {
        this.ticketCopy.ownedBy = owner;

        // Also add owner as watcher
        if (owner && !_.findWhere(this.ticketCopy.watchers, { personId: owner })) {
            this.ticketCopy.watchers = [...this.ticketCopy.watchers, { personId: owner, type: "all" }];
        }
    }

    public addTicketDue() {
        const localTimezone = this.timeService.getTimezone();
        const timezone = this.timeService.zoneNames.includes(localTimezone)
            ? localTimezone
            : this.timeService.zoneNames[0];
        this.ticketCopy.timeZoneId = timezone;
        this.ticketCopy.dueOn = this.timeService.nowAtTz(timezone).add(1, "Days").hours(20).minutes(0).seconds(0).toISOString();
    }

    public updateTicketDue(newValue: DateTimeTz) {
        this.ticketCopy.dueOn = newValue.datetime;
        this.ticketCopy.timeZoneId = newValue.timezone;
    }

    public removeTicketDue() {
        this.ticketCopy.dueOn = undefined;
        this.ticketCopy.timeZoneId = null;
    }

    /**
     * Prepend a template to description if it is defined, or set description to template if undefined.
     * @param template a markdown string
     */
    public updateDescription(template: TicketTemplate): void {
        if (this.usingNewEditor) {
            this.richTextEditor.importTemplate(template.body);
            return;
        }

        if (this.ticketCopy.description) {
            this.ticketCopy.description = `${ template.body }\n\n${ this.ticketCopy.description }`;
        } else {
            this.ticketCopy.description = template.body;
        }
    }
}
